import type { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from './useFinder.translations'
import type { FinderConfig, FinderOption } from './useFinder.types'

type I18n = ReturnType<typeof useI18n>

/**
 * The discrete screen sizes offered by the MacBook Finder size card. The
 * `real_screen_size` facet values, the option ids, and the visible labels are
 * all derived from this single list so they can never drift apart.
 */
export const MACBOOK_SCREEN_SIZES = ['13', '14', '15', '16'] as const

const sizeOptions: FinderOption[] = MACBOOK_SCREEN_SIZES.map((size) => ({
  id: size,
  label: `${size}"`,
  values: [size],
}))

/**
 * MacBook Finder config. Facet names confirmed against the live prod PLP
 * config (index `prod_us_index_backbox_model_en-us`, `productGroupingType:
 * parent`).
 *
 * Deferred: `macbook_creative` / `macbook_complex_work` (0 hits on prod — codes
 * unconfirmed) and the `conditions` criterion (`backbox_grade_value` is not
 * parent-filterable on this index — needs variant links or a variant-grouped
 * index).
 */
export function getMacbookFinderConfig(i18n: I18n): FinderConfig {
  return {
    id: 'apple-macbook',
    criteria: [
      {
        kind: 'usage',
        facetName: 'usages.code',
        title: i18n(translations.usageTitle),
        tooltip: i18n(translations.usageTooltip),
        primaryOptionsCount: 3,
        options: [
          {
            id: 'on-the-go',
            label: i18n(translations.usageOnTheGoLabel),
            values: ['macbook_mobility'],
          },
          {
            id: 'daily-use',
            label: i18n(translations.usageDailyUseLabel),
            values: ['macbook_productivity'],
          },
          {
            id: 'for-creatives',
            label: i18n(translations.usageForCreativesLabel),
            // TODO(SR-6007): `macbook_creative` returns 0 hits on prod. Code unconfirmed.
            values: ['macbook_creative'],
          },
          {
            id: 'for-complex-work',
            label: i18n(translations.usageForComplexWorkLabel),
            // TODO(SR-6007): `macbook_complex_work` returns 0 hits on prod. Code unconfirmed.
            values: ['macbook_complex_work'],
          },
        ],
      },
      {
        kind: 'budget',
        facetName: 'price',
        title: i18n(translations.budgetTitle),
        presets: [
          { id: 'budget-300', maxValue: 300 },
          { id: 'budget-450', maxValue: 450 },
          { id: 'budget-600', maxValue: 600 },
          { id: 'budget-750', maxValue: 750 },
          { id: 'budget-900', maxValue: 900 },
          { id: 'budget-1050', maxValue: 1050 },
        ],
      },
      {
        kind: 'size',
        facetName: 'real_screen_size',
        title: i18n(translations.sizeTitle),
        rangeMin: 11,
        rangeMax: 16,
        options: sizeOptions,
      },
      // TODO(SR-6007): `backbox_grade_value` is a variant attribute; parents
      // aggregate variants of different grades, so a parent-level filter matches
      // nothing on prod. Needs variant links or a variant-grouped index.
      {
        kind: 'conditions',
        facetName: 'backbox_grade_value',
        title: i18n(translations.conditionTitle),
        tooltip: i18n(translations.conditionTooltip),
        options: [
          {
            id: 'fair',
            label: i18n(translations.conditionFairLabel),
            description: i18n(translations.conditionFairDescription),
            values: ['12'],
          },
          {
            id: 'good',
            label: i18n(translations.conditionGoodLabel),
            description: i18n(translations.conditionGoodDescription),
            values: ['11'],
          },
          {
            id: 'excellent',
            label: i18n(translations.conditionExcellentLabel),
            description: i18n(translations.conditionExcellentDescription),
            values: ['10'],
          },
          {
            id: 'premium',
            label: i18n(translations.conditionPremiumLabel),
            description: i18n(translations.conditionPremiumDescription),
            values: ['9'],
          },
        ],
      },
      {
        kind: 'requirements',
        title: i18n(translations.requirementsTitle),
        secondaryFilters: [
          { facetName: 'storage' },
          { facetName: 'processor_type' },
          { facetName: 'model' },
          { facetName: 'memory' },
          { facetName: 'year_date_release' },
        ],
      },
    ],
  }
}
